/**
 * @creator/plugins-sdk/client – frontend side of element addons (browser only, never imports
 * server code).
 *
 * Component contract (all variants edit/view/cell/search):
 *   props: modelValue, settings (coerced), options, column, context, readonly, errors, id, record,
 *          locale, isNew
 *   emits: update:modelValue
 * Use `defineProps(elementProps)` / `defineEmits(elementEmits)` in `<script setup>`.
 */
import { defineComponent, h, type Component, type InjectionKey, type PropType } from 'vue';
import type { ElementManifest } from '../manifest.js';
import { TypeResolver, type Resolution } from '../resolve.js';
import { coerceSettings } from '../settings.js';
import type { LinkTarget, ResolvedLink } from '../links.js';
import type { SearchPage } from '../search.js';
import {
  i18nText,
  normalizeOptions,
  type ColumnMeta,
  type ElementConfig,
  type ElementContextKind,
  type ElementError,
  type ElementOption,
} from '../types.js';

// pure shared helpers only: the TypeBox manifest schema (validateManifest) stays out of the web bundle
export * from '../types.js';
export * from '../filter.js';
export * from '../settings.js';
export * from '../resolve.js';
export * from '../values.js';
export * from '../links.js';
export * from '../search.js';
export type { ElementManifest, LegacyTypeRule, SettingsSchema, ElementCapabilities } from '../manifest.js';

export type ElementVariant = 'edit' | 'view' | 'cell' | 'search';

export const elementProps = {
  /** API value (undefined when not set by the host) */
  modelValue: { type: null as unknown as PropType<unknown>, required: false as const },
  settings: { type: Object as PropType<Record<string, unknown>>, default: () => ({}) },
  options: { type: Array as PropType<ElementOption[]>, default: () => [] },
  column: { type: Object as PropType<ColumnMeta>, required: true as const },
  context: { type: String as PropType<ElementContextKind>, default: 'form' },
  readonly: { type: Boolean, default: false },
  errors: { type: Array as PropType<ElementError[]>, default: () => [] },
  /** DOM id of the input (the host renders `<label :for="id">`) */
  id: { type: String, default: undefined },
  /** current record values (placeholders of lookups) */
  record: { type: Object as PropType<Record<string, unknown>>, default: () => ({}) },
  locale: { type: String, default: 'de' },
  /** the form creates a new record (insert) */
  isNew: { type: Boolean, default: false },
};

/**
 * Record context offered by the host form (apps/web RecordView) to elements that act on the
 * whole record (print button, saved prints) – injected with `inject(RECORD_HOST, null)`.
 * Absent outside a record form (dev page, admin panel).
 */
export interface RecordHost {
  readonly db: string;
  readonly table: string;
  /** id of the open record (undefined while creating one) */
  readonly id: string | undefined;
  readonly isNew: boolean;
  /** open the print dialog (or directly the preview) for this record */
  print(o?: { layout?: string; preview?: boolean }): void;
  /** show a PDF (saved print) in the preview */
  preview?(o: { url: string; name: string }): void;
  /**
   * An element endpoint changed stored values of the open record (upload-fs save_value): the
   * form takes the new values and ETag as its baseline (other unsaved edits stay).
   */
  recordWritten?(o: { values: Record<string, unknown>; etag?: string }): void;
  /**
   * Function button of a column (element sqlfunction, Phase 6c): state from the table's function
   * menus (SQLFUNCTION rights, enabled, executable in NG, records of the open record). Reactive:
   * call it inside a computed.
   */
  columnFunction?(column: string): ColumnFunctionState;
}
export const RECORD_HOST: InjectionKey<RecordHost> = Symbol.for('creator.recordHost') as InjectionKey<RecordHost>;

/** State of a function button in the record form (see RecordHost.columnFunction). */
export type ColumnFunctionState =
  | { status: 'loading' }
  /** not offered: no right (SQLFUNCTION), disabled, missing, record not saved, not executable */
  | { status: 'unavailable'; label?: string; reason: string }
  | { status: 'ready'; label: string; info?: string; run(): void };

/**
 * Function dialog context offered to parameter elements (Phase 6b/6c), injected with
 * `inject(PARAM_HOST, null)`: elements that search on the server (mysql-search) call the
 * function's parameter options endpoint instead of their own element endpoint. Absent in forms.
 */
export interface ParamHost {
  readonly function: string;
  /** current values of all parameters (for `#param#` placeholders) */
  values(): Readonly<Record<string, string>>;
  search(param: string, q: { q?: string; value?: readonly string[]; page?: number; limit?: number }, signal?: AbortSignal): Promise<SearchPage>;
}
export const PARAM_HOST: InjectionKey<ParamHost> = Symbol.for('creator.paramHost') as InjectionKey<ParamHost>;

/** Open a resolved link (app route via the host router, external URL in a new tab without opener). */
export interface LinkHost {
  open(link: ResolvedLink, target: LinkTarget): void;
}
export const LINK_HOST: InjectionKey<LinkHost> = Symbol.for('creator.linkHost') as InjectionKey<LinkHost>;

export const elementEmits = {
  'update:modelValue': (_v: unknown) => true,
};

export interface FormatContext {
  settings: Record<string, unknown>;
  options: ElementOption[];
  locale: string;
}

export interface ElementClientModule {
  edit: Component;
  view?: Component;
  cell?: Component;
  search?: Component;
  /** plain text representation for view/cell defaults */
  format?(value: unknown, ctx: FormatContext): string;
}

export function defineElementClient(m: ElementClientModule): ElementClientModule {
  return m;
}

/** Default plain text formatting. */
export function defaultFormat(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Default view/cell component: text from the element's `format`. */
export function createTextView(format: ElementClientModule['format'], tag: 'span' | 'div' = 'span'): Component {
  return defineComponent({
    name: 'ElementTextView',
    props: elementProps,
    setup(props) {
      return () => {
        const text = format ? format(props.modelValue, { settings: props.settings, options: props.options, locale: props.locale }) : defaultFormat(props.modelValue);
        return h(tag, { class: text === '' ? 'text-body-secondary' : undefined }, text === '' ? '–' : text);
      };
    },
  });
}

/** Error list below an input (Bootstrap invalid-feedback, visible without sibling state). */
export const ElementErrors = defineComponent({
  name: 'ElementErrors',
  props: { errors: { type: Array as PropType<ElementError[]>, default: () => [] } },
  setup(props) {
    return () =>
      props.errors.length
        ? h('div', { class: 'invalid-feedback d-block', role: 'alert' }, props.errors.map((e) => h('div', e.message ?? e.code)))
        : null;
  },
});

/** Option label for a value. */
export function optionLabel(options: readonly ElementOption[], value: unknown, locale = 'de'): string {
  const o = options.find((x) => x.value === String(value));
  return o ? i18nText(o.label, locale) : defaultFormat(value);
}

// ---------------------------------------------------------------------------
// elementApi
// ---------------------------------------------------------------------------

/**
 * Source of the X-CSRF-Token header for cookie sessions. The API hands the per-session token out
 * in the body of POST /api/v2/auth/login and GET /api/v2/auth/me (`csrfToken`) – there is no CSRF
 * cookie – so the host app must register its session store here (apps/web: main.ts).
 */
let csrfProvider: () => string | undefined = () => undefined;

/** Set how the CSRF token is obtained (the web app passes its session store's token). */
export function setCsrfTokenProvider(fn: () => string | undefined): void {
  csrfProvider = fn;
}

/** Current CSRF token (for element requests that cannot use elementApi, e.g. uploads with progress). */
export function csrfToken(): string | undefined {
  return csrfProvider();
}

export class ElementApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body?: unknown,
  ) {
    super(code);
  }
}

export interface ElementApiOptions {
  base?: string;
  fetch?: typeof fetch;
}

export type QueryParams = Record<string, string | number | boolean | null | undefined | (string | number)[]>;

/** Tiny client for an element's own endpoints (`/api/v2/elements/{id}/…`). */
export function elementApi(id: string, opts: ElementApiOptions = {}) {
  const base = `${opts.base ?? '/api/v2/elements'}/${encodeURIComponent(id)}`;
  const doFetch = (...a: Parameters<typeof fetch>) => (opts.fetch ?? fetch)(...a);
  async function call<T>(method: 'GET' | 'POST', path: string, query?: QueryParams, body?: unknown, signal?: AbortSignal): Promise<T> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query ?? {})) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) v.forEach((x) => qs.append(k, String(x)));
      else qs.append(k, String(v));
    }
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (method !== 'GET') {
      headers['Content-Type'] = 'application/json';
      const token = csrfProvider();
      if (token) headers['X-CSRF-Token'] = token;
    }
    const q = qs.toString();
    const res = await doFetch(`${base}${path}${q ? `?${q}` : ''}`, {
      method,
      credentials: 'same-origin',
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
    const data: unknown = res.headers.get('content-type')?.includes('json') ? await res.json() : await res.text();
    if (!res.ok) {
      // API errors are { error, message } (apps/api error handler); `code` kept for element endpoints
      const body = data && typeof data === 'object' ? (data as { error?: unknown; code?: unknown }) : undefined;
      const code = typeof body?.error === 'string' ? body.error : typeof body?.code === 'string' ? body.code : `http.${res.status}`;
      throw new ElementApiError(res.status, code, data);
    }
    return data as T;
  }
  return {
    get: <T>(path: string, query?: QueryParams, signal?: AbortSignal) => call<T>('GET', path, query, undefined, signal),
    post: <T>(path: string, body?: unknown, query?: QueryParams) => call<T>('POST', path, query, body),
  };
}

// ---------------------------------------------------------------------------
// lazy client registry
// ---------------------------------------------------------------------------

export type ClientLoader = () => Promise<ElementClientModule | { default: ElementClientModule }>;

export interface ResolvedClientElement extends Resolution {
  manifest: ElementManifest;
  settings: Record<string, unknown>;
  options: ElementOption[];
}

export interface ClientRegistry {
  manifests(): ElementManifest[];
  manifest(id: string): ElementManifest | undefined;
  /** resolve a column element config (legacy types incl.) and coerce its settings */
  resolve(cfg: ElementConfig | string): ResolvedClientElement | undefined;
  /** load the client module of an element (one dynamic import per type, cached) */
  load(id: string): Promise<ElementClientModule>;
  /** component of a variant with fallbacks: view/cell → text view, search → edit */
  component(id: string, variant: ElementVariant): Promise<Component>;
  problems: string[];
}

/**
 * Build the client registry from manifests (cheap JSON, eager) and loaders (dynamic imports,
 * one chunk per element). Keys of `loaders` are element ids.
 */
export function createClientRegistry(manifests: readonly ElementManifest[], loaders: Readonly<Record<string, ClientLoader>>): ClientRegistry {
  const resolver = new TypeResolver();
  const byId = new Map<string, ElementManifest>();
  for (const m of manifests) if (resolver.add(m)) byId.set(m.id, m);
  const problems = resolver.verify();
  for (const id of byId.keys()) if (!loaders[id]) problems.push(`element ${id}: no client loader`);
  const cache = new Map<string, Promise<ElementClientModule>>();
  const textViews = new Map<string, Component>();
  const load = (id: string): Promise<ElementClientModule> => {
    let p = cache.get(id);
    if (!p) {
      const loader = loaders[id];
      if (!loader) return Promise.reject(new Error(`unknown element "${id}"`));
      p = loader().then((m) => ('edit' in m ? m : m.default));
      p.catch(() => cache.delete(id));
      cache.set(id, p);
    }
    return p;
  };
  return {
    problems,
    manifests: () => [...byId.values()],
    manifest: (id) => byId.get(id),
    resolve(cfg) {
      const c: ElementConfig = typeof cfg === 'string' ? { type: cfg } : cfg;
      const r = resolver.resolve(c);
      const manifest = r && byId.get(r.elementId);
      if (!r || !manifest) return undefined;
      return {
        ...r,
        manifest,
        settings: coerceSettings(manifest.settingsSchema, c.settings, r.implied).settings,
        options: normalizeOptions(c.options),
      };
    },
    load,
    async component(id, variant) {
      const m = await load(id);
      if (variant === 'edit') return m.edit;
      if (variant === 'search') return m.search ?? m.edit;
      const own = m[variant] ?? m.view;
      if (own) return own;
      const key = `${id}/${variant}`;
      let c = textViews.get(key);
      if (!c) textViews.set(key, (c = createTextView(m.format, variant === 'view' ? 'div' : 'span')));
      return c;
    },
  };
}
